import { useMemo } from "react";
import { StatBlock } from "./StatBlock";
import { TooltipProvider } from "@/components/ui/tooltip";
import { distanceUnit, distanceValue, fmtDuration, fmtSpeed } from "../lib/format";
import { METRIC_COLORS } from "../lib/designTokens";
import { getMetricVisual } from "../lib/metricVisuals";
import { getSportKey } from "../lib/sportVisuals";
import { usesPaceAnalytics } from "../lib/analyticsRate";
import {
  MOTION_TOOLTIPS,
  formatCadenceRpm,
  formatMotionSourceSummary,
  isLegacyMotionSummary,
  resolveElapsedSeconds,
  resolveMaxCadence,
  resolveMovingSpeedMs,
  resolveMovingTimeSeconds,
  resolvePedallingCadence,
} from "../lib/motionMetrics";
import { fmtIF, resolveActivityPowerMetrics } from "../lib/powerMetrics";
import {
  cadencePresentation,
  positiveMetric,
  showCyclingLoadMetrics,
  showRecordedPowerSummary,
} from "../lib/sportAnalyticsGate";

const SPORT_DETAIL_ORDER = {
  cycling: [
    "distance",
    "movingTime",
    "elapsedTime",
    "elevation",
    "avgSpeed",
    "maxSpeed",
    "avgHr",
    "maxHr",
    "avgPower",
    "maxPower",
    "avgCadence",
    "maxCadence",
    "avgTemp",
    "minTemp",
    "maxTemp",
    "calories",
    "np",
    "if",
    "tss",
  ],
  running: [
    "distance",
    "movingTime",
    "elapsedTime",
    "avgSpeed",
    "maxSpeed",
    "avgHr",
    "maxHr",
    "avgCadence",
    "maxCadence",
    "elevation",
    "calories",
    "avgPower",
    "maxPower",
    "np",
    "if",
    "tss",
    "avgTemp",
    "minTemp",
    "maxTemp",
  ],
  swimming: [
    "distance",
    "movingTime",
    "elapsedTime",
    "avgSpeed",
    "maxSpeed",
    "laps",
    "avgHr",
    "maxHr",
    "calories",
    "avgTemp",
    "minTemp",
    "maxTemp",
  ],
  walking: [
    "distance",
    "movingTime",
    "elapsedTime",
    "avgSpeed",
    "maxSpeed",
    "elevation",
    "avgHr",
    "maxHr",
    "calories",
    "avgCadence",
    "maxCadence",
    "avgTemp",
    "minTemp",
    "maxTemp",
  ],
  hiking: [
    "distance",
    "movingTime",
    "elapsedTime",
    "elevation",
    "avgSpeed",
    "maxSpeed",
    "avgHr",
    "maxHr",
    "calories",
    "avgCadence",
    "maxCadence",
    "avgTemp",
    "minTemp",
    "maxTemp",
  ],
  other: [
    "distance",
    "movingTime",
    "elapsedTime",
    "elevation",
    "avgSpeed",
    "maxSpeed",
    "avgHr",
    "maxHr",
    "calories",
    "avgPower",
    "maxPower",
    "avgCadence",
    "maxCadence",
    "np",
    "if",
    "tss",
    "avgTemp",
    "minTemp",
    "maxTemp",
  ],
};

function orderIndex(sportKey, metricKey) {
  const order = SPORT_DETAIL_ORDER[sportKey] || SPORT_DETAIL_ORDER.other;
  const idx = order.indexOf(metricKey);
  return idx === -1 ? 999 : idx;
}

export const ActivitySummaryStats = ({
  activity,
  unitSystem = "metric",
  ridePowerMetrics: ridePowerMetricsProp = null,
  userFtp = null,
  temperatureStats = null,
}) => {
  const distanceLabel = distanceUnit(unitSystem);
  const sportKey = getSportKey(activity);
  const paceMode = usesPaceAnalytics(activity);
  const speedVisual = getMetricVisual(paceMode ? "pace" : "speed");
  const durationVisual = getMetricVisual("duration");
  const distanceVisual = getMetricVisual("distance");
  const elevationVisual = getMetricVisual("elevation");
  const hrVisual = getMetricVisual("hr");
  const powerVisual = getMetricVisual("power");
  const cadenceVisual = getMetricVisual("cadence");
  const caloriesVisual = getMetricVisual("calories");
  const tempVisual = getMetricVisual("neutral");

  const movingSpeedMs = resolveMovingSpeedMs(activity);
  const pedallingCadence = resolvePedallingCadence(activity);
  const maxCadence = resolveMaxCadence(activity);
  const movingTimeSeconds = resolveMovingTimeSeconds(activity);
  const elapsedSeconds = resolveElapsedSeconds(activity);
  const motionSourceSummary = formatMotionSourceSummary(activity);
  const legacySummary = isLegacyMotionSummary(activity);

  const ridePowerMetrics =
    ridePowerMetricsProp ?? resolveActivityPowerMetrics(activity, userFtp);

  const cadenceCopy = cadencePresentation(activity);
  const showPedallingCadence = cadenceCopy.show && (pedallingCadence != null || activity?.has_cadence);
  const showMaxCadence = cadenceCopy.show && maxCadence != null;
  const speedLabel = paceMode ? "Avg Pace" : "Avg Speed";
  const maxSpeedLabel = paceMode ? "Best Pace" : "Max Speed";
  const lapCount = activity?.lap_count ?? activity?.laps?.length;

  const metrics = useMemo(() => {
    const items = [];

    const push = (key, config) => {
      if (config.visible === false) return;
      items.push({ key, ...config });
    };

    push("distance", {
      label: "Distance",
      value:
        activity.total_distance_m != null
          ? distanceValue(activity.total_distance_m, unitSystem).toFixed(2)
          : null,
      unit: distanceLabel,
      accent: distanceVisual.accent,
      testId: "stat-distance",
      visible: activity.total_distance_m != null,
    });

    push("movingTime", {
      label: "Moving Time",
      value: fmtDuration(movingTimeSeconds),
      accent: durationVisual.accent,
      tooltip: MOTION_TOOLTIPS.movingTime,
      testId: "stat-moving-time",
      visible: movingTimeSeconds != null,
    });

    push("elapsedTime", {
      label: "Elapsed Time",
      value: fmtDuration(elapsedSeconds),
      accent: durationVisual.accent,
      tooltip: MOTION_TOOLTIPS.elapsedTime,
      testId: "stat-elapsed-time",
      visible: elapsedSeconds != null,
    });

    push("elevation", {
      label: "Elevation Gain",
      value: activity.elevation_gain_m?.toFixed(0),
      unit: "m",
      accent: elevationVisual.accent,
      testId: "stat-elevation",
      visible: positiveMetric(activity.elevation_gain_m),
    });

    push("avgSpeed", {
      label: speedLabel,
      value: fmtSpeed(movingSpeedMs, activity.sport, unitSystem),
      accent: speedVisual.accent,
      tooltip: MOTION_TOOLTIPS.avgSpeed,
      testId: "stat-avg-speed",
      visible: movingSpeedMs != null,
    });

    push("maxSpeed", {
      label: maxSpeedLabel,
      value: fmtSpeed(activity.max_speed_ms, activity.sport, unitSystem),
      accent: speedVisual.accent,
      testId: "stat-max-speed",
      visible: activity.max_speed_ms != null,
    });

    if (activity.has_hr) {
      push("avgHr", {
        label: "Avg HR",
        value: activity.avg_hr != null ? Math.round(activity.avg_hr) : null,
        unit: "bpm",
        accent: hrVisual.accent,
        testId: "stat-avg-hr",
        visible: activity.avg_hr != null,
      });
      push("maxHr", {
        label: "Max HR",
        value: activity.max_hr != null ? Math.round(activity.max_hr) : null,
        unit: "bpm",
        accent: hrVisual.accent,
        testId: "stat-max-hr",
        visible: activity.max_hr != null,
      });
    }

    if (showRecordedPowerSummary(activity)) {
      push("avgPower", {
        label: "Avg Power",
        value: activity.avg_power != null ? Math.round(activity.avg_power) : null,
        unit: "W",
        accent: powerVisual.accent,
        testId: "stat-avg-power",
        visible: activity.avg_power != null,
      });
      push("maxPower", {
        label: "Max Power",
        value: activity.max_power != null ? Math.round(activity.max_power) : null,
        unit: "W",
        accent: powerVisual.accent,
        testId: "stat-max-power",
        visible: activity.max_power != null,
      });
    }

    if (showPedallingCadence) {
      push("avgCadence", {
        label: cadenceCopy.avgLabel,
        value: formatCadenceRpm(pedallingCadence),
        unit: cadenceCopy.unit,
        accent: cadenceVisual.accent,
        tooltip: MOTION_TOOLTIPS.avgCadence,
        testId: "stat-avg-cadence",
        visible: pedallingCadence != null,
      });
    }

    if (showMaxCadence) {
      push("maxCadence", {
        label: cadenceCopy.maxLabel,
        value: formatCadenceRpm(maxCadence),
        unit: cadenceCopy.unit,
        accent: cadenceVisual.accent,
        testId: "stat-max-cadence",
      });
    }

    if (temperatureStats) {
      push("avgTemp", {
        label: "Avg Temp",
        value: temperatureStats.avg,
        unit: "°C",
        accent: tempVisual.accent,
        testId: "stat-avg-temp",
      });
      push("minTemp", {
        label: "Min Temp",
        value: temperatureStats.min,
        unit: "°C",
        accent: tempVisual.accent,
        testId: "stat-min-temp",
      });
      push("maxTemp", {
        label: "Max Temp",
        value: temperatureStats.max,
        unit: "°C",
        accent: tempVisual.accent,
        testId: "stat-max-temp",
      });
    }

    push("calories", {
      label: "Calories",
      value: activity.calories != null ? Math.round(activity.calories) : null,
      unit: "kcal",
      accent: caloriesVisual.accent,
      testId: "stat-calories",
      visible: activity.calories != null,
    });

    if (sportKey === "swimming" && lapCount > 0) {
      push("laps", {
        label: "Laps",
        value: String(Math.round(lapCount)),
        accent: getMetricVisual("laps").accent,
        testId: "stat-laps",
      });
    }

    if (ridePowerMetrics && showCyclingLoadMetrics(activity)) {
      push("np", {
        label: "Norm. Power",
        value: ridePowerMetrics.normalized_power,
        unit: "W",
        accent: METRIC_COLORS.normalizedPower,
        testId: "stat-np",
      });
      push("if", {
        label: "Intensity",
        value: fmtIF(ridePowerMetrics.intensity_factor),
        accent: METRIC_COLORS.tss,
        testId: "stat-if",
      });
      push("tss", {
        label: "TSS",
        value: ridePowerMetrics.tss,
        accent: METRIC_COLORS.tss,
        testId: "stat-tss",
      });
    }

    return items.sort(
      (a, b) => orderIndex(sportKey, a.key) - orderIndex(sportKey, b.key)
    );
  }, [
    activity,
    distanceLabel,
    sportKey,
    speedLabel,
    maxSpeedLabel,
    movingSpeedMs,
    movingTimeSeconds,
    elapsedSeconds,
    pedallingCadence,
    maxCadence,
    showPedallingCadence,
    showMaxCadence,
    cadenceCopy,
    temperatureStats,
    ridePowerMetrics,
    lapCount,
    distanceVisual.accent,
    durationVisual.accent,
    elevationVisual.accent,
    speedVisual.accent,
    hrVisual.accent,
    powerVisual.accent,
    cadenceVisual.accent,
    caloriesVisual.accent,
    tempVisual.accent,
    unitSystem,
  ]);

  return (
    <div className="space-y-3">
      <TooltipProvider delayDuration={150}>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {metrics.map((metric) => (
            <StatBlock
              key={metric.key}
              label={metric.label}
              value={metric.value}
              unit={metric.unit}
              accent={metric.accent}
              tooltip={metric.tooltip}
              testId={metric.testId}
            />
          ))}
        </div>
      </TooltipProvider>

      {motionSourceSummary && (
        <div
          className="app-card-elevated inline-flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5"
          data-testid="motion-source-summary"
          aria-label={`Moving time calculation source: ${motionSourceSummary}`}
        >
          <span className="card-label">Moving time source</span>
          <span className="text-[11px] font-mono uppercase tracking-wide text-dark-text-secondary">
            {motionSourceSummary}
          </span>
        </div>
      )}

      {legacySummary && !motionSourceSummary && (
        <div
          className="app-card-elevated inline-flex items-center gap-2 px-4 py-2.5"
          data-testid="motion-legacy-label"
          title={MOTION_TOOLTIPS.legacyCalculation}
        >
          <span className="card-label">Data note</span>
          <span className="text-[11px] font-mono uppercase tracking-wide text-dark-text-muted">
            Legacy calculation
          </span>
        </div>
      )}
    </div>
  );
};
